import axios from "../axios";
import * as url from "./url";

// TBV-938 — permanent short links. Shared by the Share modal, the
// Survey / Knowledge Check / Messaging list Copy Link actions and the legacy
// on-demand dialog. The backend is authoritative for auth and builds the URL;
// this only resolves it, validates it (defense in depth) and copies it.

export const SHORT_LINK_ITEM_TYPES = {
  TRIVVY: "TRIVVY",
  LIBRARY: "LIBRARY",
};

// Which sharing surface asks — the BE uses it only to pick the URL form.
// Every surface gets the SAME code; only the allowlisted ?medium= differs.
export const SHORT_LINK_SOURCES = {
  LIST_MENU: "LIST_MENU",
  SHARE_MODAL: "SHARE_MODAL",
  QR_CODE: "QR_CODE",
  WHATSAPP: "WHATSAPP",
  SLACK: "SLACK",
  TEAMS: "TEAMS",
};

// The exact ?medium= each surface's URL must carry — the same values its
// long URL carried before (lists: none; Teams shared as "slack").
const SHORT_LINK_SOURCE_MEDIUMS = {
  LIST_MENU: null,
  SHARE_MODAL: "copy",
  QR_CODE: "qrcode",
  WHATSAPP: "whatsapp",
  SLACK: "slack",
  TEAMS: "slack",
};

export const SHORT_LINK_ERROR_KINDS = {
  BAD_REQUEST: "BAD_REQUEST",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  RATE_LIMITED: "RATE_LIMITED",
  SERVER: "SERVER",
  NETWORK: "NETWORK",
  INVALID_RESPONSE: "INVALID_RESPONSE",
  CANCELLED: "CANCELLED",
  CLIPBOARD_UNAVAILABLE: "CLIPBOARD_UNAVAILABLE",
  CLIPBOARD_FAILED: "CLIPBOARD_FAILED",
};

// Only transient storage/availability failures may offer the original long
// link; auth, permission, missing-item, rate-limit and config errors never do.
export const SHORT_LINK_FALLBACK_ERROR_KINDS = [
  SHORT_LINK_ERROR_KINDS.SERVER,
  SHORT_LINK_ERROR_KINDS.NETWORK,
];

// Trusted short-link hosts per environment (the BE's
// PUBLIC_SHORT_LINK_BASE_URL values). Deliberately NOT taken from the API
// response or window.location. Add the dedicated short domain here once it
// is routed.
const SHORT_LINK_ALLOWED_ORIGINS = [
  "https://trivvy.ouroffice.io",
  "https://portal.ouroffice.io",
  "https://qaportal.ouroffice.io",
  "https://devportal.ouroffice.io",
];
// Plain HTTP is only ever accepted for local development.
const LOCAL_SHORT_LINK_ORIGINS = ["http://localhost:3000"];

const SHORT_LINK_PATH_PREFIXES = {
  TRIVVY: "t",
  LIBRARY: "l",
};
const SHORT_CODE_PATTERN = /^[A-Za-z0-9_-]{12}$/;
const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

const createShortLinkError = (kind, extra) => {
  const error = new Error(kind);
  error.kind = kind;
  if (extra) {
    Object.assign(error, extra);
  }
  return error;
};

const getAllowedShortLinkOrigins = () => {
  if (window.location.hostname === "localhost") {
    return SHORT_LINK_ALLOWED_ORIGINS.concat(LOCAL_SHORT_LINK_ORIGINS);
  }
  return SHORT_LINK_ALLOWED_ORIGINS;
};

/**
 * Strictly validates a short URL before it is shown, copied or encoded:
 * trusted origin, HTTPS (HTTP only for local dev), `/t/{code}` or `/l/{code}`
 * for the expected item type, no credentials or fragment, and EXACTLY the
 * `?medium=` the requesting surface should carry (none for lists / Library).
 */
export const validateTrivvyShortUrl = (urlString, itemType, source = SHORT_LINK_SOURCES.LIST_MENU) => {
  if (typeof urlString !== "string" || !urlString) {
    return false;
  }
  let parsedUrl;
  try {
    parsedUrl = new URL(urlString);
  } catch (e) {
    return false;
  }
  if (getAllowedShortLinkOrigins().indexOf(parsedUrl.origin) === -1) {
    return false;
  }
  if (parsedUrl.protocol !== "https:" && LOCAL_SHORT_LINK_ORIGINS.indexOf(parsedUrl.origin) === -1) {
    return false;
  }
  if (parsedUrl.username || parsedUrl.password || parsedUrl.hash) {
    return false;
  }
  const pathParts = parsedUrl.pathname.split("/");
  if (
    pathParts.length !== 3 ||
    pathParts[1] !== SHORT_LINK_PATH_PREFIXES[itemType] ||
    !SHORT_CODE_PATTERN.test(pathParts[2])
  ) {
    return false;
  }
  const expectedMedium =
    itemType === SHORT_LINK_ITEM_TYPES.TRIVVY ? SHORT_LINK_SOURCE_MEDIUMS[source] : null;
  if (expectedMedium) {
    return parsedUrl.search === `?medium=${expectedMedium}`;
  }
  return !parsedUrl.search;
};

const getErrorKindFromStatus = (status) => {
  if (status === 400) return SHORT_LINK_ERROR_KINDS.BAD_REQUEST;
  if (status === 401) return SHORT_LINK_ERROR_KINDS.UNAUTHORIZED;
  if (status === 403) return SHORT_LINK_ERROR_KINDS.FORBIDDEN;
  if (status === 404) return SHORT_LINK_ERROR_KINDS.NOT_FOUND;
  if (status === 409) return SHORT_LINK_ERROR_KINDS.CONFLICT;
  if (status === 429) return SHORT_LINK_ERROR_KINDS.RATE_LIMITED;
  return SHORT_LINK_ERROR_KINDS.SERVER;
};

const normalizeShortLinkError = (error) => {
  // axios 0.27 rejects an aborted request with code ERR_CANCELED.
  if (error && error.code === "ERR_CANCELED") {
    return createShortLinkError(SHORT_LINK_ERROR_KINDS.CANCELLED);
  }
  if (!error || !error.response) {
    return createShortLinkError(SHORT_LINK_ERROR_KINDS.NETWORK);
  }
  const kind = getErrorKindFromStatus(error.response.status);
  if (kind === SHORT_LINK_ERROR_KINDS.RATE_LIMITED) {
    const headers = error.response.headers || {};
    const retryAfterSeconds = parseInt(headers["retry-after"], 10);
    return createShortLinkError(kind, {
      retryAfterSeconds: isNaN(retryAfterSeconds) ? null : retryAfterSeconds,
    });
  }
  return createShortLinkError(kind);
};

/**
 * POST copy-link and return the validated { itemType, shortUrl }.
 * Rejects with an Error carrying `.kind` (SHORT_LINK_ERROR_KINDS).
 */
export const resolveTrivvyShortUrl = ({ itemType, itemId, source, signal }) => {
  if (
    !SHORT_LINK_PATH_PREFIXES[itemType] ||
    typeof itemId !== "string" ||
    !OBJECT_ID_PATTERN.test(itemId) ||
    (source && !SHORT_LINK_SOURCES[source])
  ) {
    return Promise.reject(createShortLinkError(SHORT_LINK_ERROR_KINDS.BAD_REQUEST));
  }
  const body = { itemType, itemId };
  if (source) {
    body.source = source;
  }
  return axios
    .post(url.copyTrivvyLink, body, { signal })
    .catch((error) => {
      throw normalizeShortLinkError(error);
    })
    .then((res) => {
      const linkData = res && res.data && res.data.data;
      if (
        !linkData ||
        linkData.itemType !== itemType ||
        !validateTrivvyShortUrl(linkData.shortUrl, itemType, source || SHORT_LINK_SOURCES.LIST_MENU)
      ) {
        throw createShortLinkError(SHORT_LINK_ERROR_KINDS.INVALID_RESPONSE);
      }
      return { itemType: linkData.itemType, shortUrl: linkData.shortUrl };
    });
};

/**
 * Must be called directly inside the user's click handler (Safari/mobile
 * only allow clipboard writes during the gesture). Resolves only when the
 * write actually succeeded — never reports success otherwise.
 */
export const copyTextToClipboard = (text) => {
  if (
    !window.isSecureContext ||
    !window.navigator.clipboard ||
    !window.navigator.clipboard.writeText
  ) {
    return Promise.reject(createShortLinkError(SHORT_LINK_ERROR_KINDS.CLIPBOARD_UNAVAILABLE));
  }
  return window.navigator.clipboard.writeText(text).catch(() => {
    throw createShortLinkError(SHORT_LINK_ERROR_KINDS.CLIPBOARD_FAILED);
  });
};
